import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Upload, X, Loader2, Crop, Check, RotateCcw } from 'lucide-react';

const ImageCropUploader = ({ 
  onUpload, 
  accept = "image/*", 
  label = "Görsel Seç",
  aspectRatio = undefined, // 1 for square, 16/9 for landscape, etc. undefined for free
  cropEnabled = true
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showCropper, setShowCropper] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  
  const fileInputRef = useRef(null);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const handleFileSelect = (files) => {
    if (!files || files.length === 0) return;
    
    const file = files[0];
    if (!file.type.startsWith('image/')) {
      setError('Lütfen bir görsel dosyası seçin');
      return;
    }
    
    setError('');
    setSelectedFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
      if (cropEnabled) {
        setShowCropper(true);
        // Reset crop
        setCrop(null);
        setCompletedCrop(null);
      } else {
        // Direct upload without cropping
        uploadFile(file);
      }
    };
    reader.readAsDataURL(file);
  };

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    
    // Set initial crop to center 80% of image
    const cropWidth = Math.min(width * 0.8, height * 0.8);
    const cropHeight = aspectRatio ? cropWidth / aspectRatio : cropWidth;
    
    const initialCrop = {
      unit: 'px',
      x: (width - cropWidth) / 2,
      y: (height - Math.min(cropHeight, height * 0.8)) / 2,
      width: cropWidth,
      height: Math.min(cropHeight, height * 0.8)
    };
    
    setCrop(initialCrop);
  }, [aspectRatio]);

  const getCroppedImage = useCallback(() => {
    if (!imgRef.current || !completedCrop?.width || !completedCrop?.height) {
      return null;
    }

    const image = imgRef.current;
    const canvas = canvasRef.current;
    
    if (!canvas) return null;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Set canvas size to crop size
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  }, [completedCrop]);

  const uploadFile = async (file) => {
    setUploading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${backendUrl}/api/admin/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        const fullUrl = `${backendUrl}${data.url}`;
        onUpload(fullUrl);
        resetState();
      } else {
        const errorData = await res.json();
        setError(errorData.detail || 'Yükleme başarısız');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Yükleme hatası');
    } finally {
      setUploading(false);
    }
  };

  const handleCropAndUpload = async () => {
    if (!completedCrop?.width || !completedCrop?.height) {
      // If no crop selected, upload original
      if (selectedFile) {
        await uploadFile(selectedFile);
      }
      return;
    }

    setUploading(true);
    setError('');

    try {
      const croppedBlob = await getCroppedImage();
      
      if (!croppedBlob) {
        setError('Kırpma işlemi başarısız');
        setUploading(false);
        return;
      }

      const croppedFile = new File(
        [croppedBlob], 
        selectedFile?.name || 'cropped-image.jpg', 
        { type: 'image/jpeg' }
      );
      
      await uploadFile(croppedFile);
    } catch (err) {
      console.error('Crop error:', err);
      setError('Kırpma hatası');
      setUploading(false);
    }
  };

  const handleSkipCrop = async () => {
    if (selectedFile) {
      await uploadFile(selectedFile);
    }
  };

  const resetState = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setCrop(null);
    setCompletedCrop(null);
    setShowCropper(false);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  // Cropper Modal
  if (showCropper && previewUrl) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Crop size={20} />
              <h3 className="font-medium">Görseli Kırp</h3>
            </div>
            <button 
              onClick={resetState}
              className="p-1 hover:bg-gray-100 rounded"
              disabled={uploading}
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Crop Area */}
          <div className="flex-1 overflow-auto p-4 bg-gray-100 flex items-center justify-center">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspectRatio}
              className="max-w-full"
            >
              <img
                src={previewUrl}
                alt="Crop preview"
                onLoad={onImageLoad}
                style={{ maxHeight: '60vh', maxWidth: '100%' }}
              />
            </ReactCrop>
          </div>
          
          {/* Hidden canvas for cropping */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          
          {/* Instructions */}
          <div className="px-4 py-2 bg-gray-50 text-center text-sm text-gray-600">
            Görselin gösterilecek kısmını seçmek için çerçeveyi sürükleyin
          </div>
          
          {/* Actions */}
          <div className="p-4 border-t flex items-center justify-between gap-3">
            <button
              onClick={handleSkipCrop}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 text-sm border hover:bg-gray-50 disabled:opacity-50"
            >
              <RotateCcw size={16} />
              Kırpmadan Yükle
            </button>
            
            <div className="flex gap-2">
              <button
                onClick={resetState}
                disabled={uploading}
                className="px-4 py-2 text-sm border hover:bg-gray-50 disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={handleCropAndUpload}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-black text-white hover:bg-gray-800 disabled:bg-gray-400"
              >
                {uploading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Kırp ve Yükle
                  </>
                )}
              </button>
            </div>
          </div>
          
          {error && (
            <div className="px-4 pb-4">
              <p className="text-sm text-red-500 text-center">{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // File Select UI
  return (
    <div className="space-y-2">
      <div
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        className={`
          border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
          ${dragOver ? 'border-black bg-gray-50' : 'border-gray-300 hover:border-gray-400'}
          ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
          disabled={uploading}
        />
        
        {uploading ? (
          <div className="flex flex-col items-center gap-2 py-2">
            <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
            <span className="text-sm text-gray-500">Yükleniyor...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-2">
            <Upload className="w-6 h-6 text-gray-400" />
            <span className="text-sm text-gray-500">{label}</span>
            <span className="text-xs text-gray-400">
              {cropEnabled ? 'Seçtikten sonra kırpabilirsiniz' : 'veya sürükle bırak'}
            </span>
          </div>
        )}
      </div>
      
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};

export default ImageCropUploader;
